import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const forced = !!user?.must_change_password;

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      updateUser({ ...user, must_change_password: false });
      toast.success("Password updated");
      navigate("/");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F9FAFB]">
      <div className="w-full max-w-sm" data-testid="change-password-page">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/renova-icon.png" alt="" className="w-10 h-10 object-contain" />
          <span className="font-heading font-extrabold text-lg">Renova Eco Systems</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <ShieldCheck className="w-5 h-5 text-[#1B4332]" />
          <h2 className="font-heading text-2xl font-bold tracking-tight">
            {forced ? "Set a new password" : "Change password"}
          </h2>
        </div>
        <p className="text-sm text-gray-500 mb-6">
          {forced
            ? "Your account uses a temporary password. Choose a new one to continue."
            : "Update the password for your account."}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="current">{forced ? "Temporary password" : "Current password"}</Label>
            <Input id="current" type="password" required value={current} data-testid="current-password"
              onChange={(e) => setCurrent(e.target.value)} placeholder="••••••••" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="next">New password</Label>
            <Input id="next" type="password" required value={next} data-testid="new-password"
              onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm">Confirm new password</Label>
            <Input id="confirm" type="password" required value={confirm} data-testid="confirm-password"
              onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" />
          </div>
          <Button type="submit" disabled={loading} data-testid="change-password-submit"
            className="w-full bg-[#0A0A0A] hover:bg-gray-800 h-11">
            {loading ? "Updating…" : "Update password"}
          </Button>
        </form>

        {forced && (
          <button type="button" onClick={doLogout} data-testid="cp-logout"
            className="w-full mt-3 h-10 flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-800">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        )}
      </div>
    </div>
  );
}
